import { useCallback, useMemo } from 'react';

import Rect from '@lyearn/core/components/Rect';
import { cn } from '@lyearn/style';
import { Box } from '@lyearn/ui';
import Table from '@/components/Table';
import { TableProps } from '@/components/Table/types';
import {
  getEmptyChildrenRowCellPadding,
  getNestedTableDepthStyles,
} from '@/modules/report/charts/Table/styles';
import { getTableChartComponents } from '@/modules/report/charts/Table/utils/table/getTableChartComponents';

import AddMicroSkill from './components/AddMicroSkill';
import MicroSkillFetcher from './components/MicroSkillFetcher';
import { MicroSkillContextProvider } from './context/MicroSkillsContext';
import { adaptMicroSkillDatum } from './adapter';
import { tableStyles } from './styles';
import { MicroSkillsProps } from './types';

const components: TableProps['components'] = getTableChartComponents({ className: 'me-8' });

const MicroSkillsTableView = <T extends object>(props: MicroSkillsProps<T>) => {
  const {
    columns,
    canAddMicroSkill,
    uiState,
    actions,
    microSkillsMap,
    hierarchyMicroSkillsMap,
    rootMicroSkillIds,
    setUIState,
    onExpandedKeysChange,
    expandedKeys,
    className,
  } = props;

  const rowRenderer = useCallback(
    ({ rowData, cells }: TSFixMe) => {
      if (rowData?.type === 'ACTION') {
        return <AddMicroSkill onClick={actions?.onAddMicroSkill} />;
      }
      if (rowData?.type === 'LAZY_LOAD') {
        return (
          <MicroSkillFetcher cells={cells} fetch={actions?.fetchMicroSkills} rowData={rowData} />
        );
      }
      return cells;
    },
    [actions?.fetchMicroSkills, actions?.onAddMicroSkill],
  );

  const data = useMemo(
    () =>
      adaptMicroSkillDatum({
        microSkillsMap,
        hierarchyMicroSkillsMap,
        rootMicroSkillIds,
        canAddMicroSkill,
      }),
    [canAddMicroSkill, hierarchyMicroSkillsMap, microSkillsMap, rootMicroSkillIds],
  );

  const rowClassName = useCallback(
    ({ rowData }: any) =>
      cn(rowData.children?.length ? '' : getEmptyChildrenRowCellPadding({}), 'group'),
    [],
  );

  return (
    <MicroSkillContextProvider actions={actions} setUIState={setUIState} uiState={uiState}>
      <Box className={cn('relative flex flex-1 flex-col', className)}>
        <Rect className="flex-1">
          {({ width, height }) => {
            return (
              <Table
                key="Microskills-table"
                className={cn(getNestedTableDepthStyles({}), tableStyles)}
                columns={columns}
                components={components}
                data={data}
                estimatedRowHeight={60}
                expandColumnKey="name"
                expandedRowKeys={expandedKeys}
                headerHeight={0}
                maxHeight={height}
                rowClassName={rowClassName}
                rowHeight={60}
                rowKey="id"
                rowRenderer={rowRenderer}
                width={width}
                onExpandedRowsChange={onExpandedKeysChange}
              />
            );
          }}
        </Rect>
      </Box>
    </MicroSkillContextProvider>
  );
};

export default MicroSkillsTableView;
